import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import _, { capitalize, isEmpty, noop } from 'lodash';
import { FieldTypes } from 'utils/formUtils';
import { Tooltip, Form } from 'antd';
import BaseModalForm from 'components/BaseModalForm';
import FindingAPI from 'restAPI/FindingAPI';
import message from 'components/CustomMessage';
import { hasPermission } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import { FINDING_UPDATE_TASK_TYPES, SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES, TOOLS_NOT_FOR_FUTURE_FINDINGS , SIMILAR_FINDINGS_TYPES} from './Constants';
import { MODAL_SIZE_ULTRA, MODAL_SIZE_SMALL } from 'constants/Layout';
import SimilarFindingsTable from './SimilarFindingsTable';
import { useFindingScopeFields } from 'views/Findings/utils';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { getVocabulary } from 'components/Vocabulary';

const SeverityCategoryChangeModal = forwardRef((props, ref) => {
    const thisRef = useRef();
    useImperativeHandle(ref, () => ({ ...thisRef.current }), []);
    const [findingsCount, setFindingsCount] = useState(0);
    const [showFindingsTable, setShowFindingsTable] = useState(false);
    const similarFindingsToNotUpdate = useRef([]);
    const [updateType, setUpdateType] = useState(SIMILAR_FINDINGS_TYPES.RULE_BASED);
    const [tagCondition, setTagCondition] = useState('EQUALS');

    const featureFlags = useFeatureFlag();

    const [scope, setScope] = useState('FINDING');
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [rule, setRule] = useState(undefined);
    const findingUpdateTaskType = props.type === 'severity' ? FINDING_UPDATE_TASK_TYPES.UPDATE_SEVERITY : FINDING_UPDATE_TASK_TYPES.UPDATE_CATEGORY;

    useEffect(() => {
        if(!scope || scope === 'FINDING' || !findingsCount){
            setRule(undefined);
            return noop;
        }
        const { promise, cancel } = FindingAPI.similarFindings.getRule({ 
            findingId: props.findingId,
            taskType: `update_${props.type}`,
            scope,
        });
        promise.then(setRule);
        return cancel;
    }, [props.findingId, scope, props.type, findingsCount]);

    useEffect(() => {
        if(!isEmpty(rule)) {
            form.setFieldsValue({
                applyOnFutureFindings: false
            });
        }
    },[rule, form]);

    const scopeOnChange = (_scope) => {
        setScope(_scope);
        if (_scope === 'FINDING') {
            setShowFindingsTable(false);
            setFindingsCount(0);
        }
    };

    const redirectToBucket = () => {
        setShowFindingsTable(true);
    };

    const payload = useMemo(() => ({
        scope,
        updateType,
        contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
        equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
        findingId: props.findingId,
        findingUpdateTaskType,
        [props.type]: props[props.type],
    }), [scope, updateType, tagCondition, props, findingUpdateTaskType])

    const permission = hasPermission(props.type === 'category' ? PermissionSetMap.SIMILAR_FINDINGS_CATEGORY : PermissionSetMap.SIMILAR_FINDINGS_SEVERITY);
    
    const scopeParams = useMemo(() => ({
        finding: props.finding,
        helpText: !permission ? `Similar findings ${props.type} permission is required to change scope.` : '',
        permission,
        scopeOnChange,
        redirectToBucket,
        similarFindings: findingsCount,
        showFindingsTable,
        scope,
        updateType,
        setUpdateType,
        setTagCondition
    }), [findingsCount, permission, props, scope, showFindingsTable, updateType])

    const scopeFields = useFindingScopeFields({payload, ...scopeParams});

    useEffect(() => {
        if (scope !== 'FINDING') {
            setLoading(true);
            const { promise, cancel } = FindingAPI.similarFindings.count(payload);
            promise.then((_findings) => setFindingsCount(_findings || 0)).catch((e) => {
                console.error(e)
                if(e.response.data.message.startsWith("Simila")){
                    message.error("Similar Finding Rules cannot be applied to this finding")
                    setFindingsCount(undefined)
                }
            }).finally(() => setLoading(false));

            return cancel;
        }
    }, [scope, payload]);

    const fields = useMemo(() => {
        const fields = [{
            name: "note",
            label: "Note",
            type: FieldTypes.TEXT_AREA,
            required: true,
            customLengthRule: {
                max: 1000,
                message: "Maximum length is 1000 characters" 
            },
          }];

        if(featureFlags.VERSIONS){
            fields.push({
                label: "Apply to all unresolved versioned Findings",
                name: "applyToVersionFindings",
                type: FieldTypes.SWITCH,
                inline: true
            })
        }

        fields.push(...scopeFields)
        
        if (scope !== 'FINDING' && !_.isUndefined(findingsCount) && TOOLS_NOT_FOR_FUTURE_FINDINGS.indexOf(_.toLower(props.toolName || props.source)) === -1) {
            if (updateType === SIMILAR_FINDINGS_TYPES.RULE_BASED) {
                fields.push({
                    name: "applyOnFutureFindings",
                    label: <Tooltip title={!isEmpty(rule) ?
                        `Rule already exists to set all future findings ${props.type} to ${capitalize(rule.otherProperties?.[props.type === 'category' ? 'ARMORCODE_CATEGORY' : 'SEVERITY'])}.`
                        : undefined}>Apply to all future findings</Tooltip>,
                    type: FieldTypes.BOOLEAN,
                    inline: true,
                    disabled: !isEmpty(rule),
                    permission: PermissionSetMap.SIMILAR_FINDINGS_RULE_UPDATE,
                });
            }

            const tableprops = {
                title: `${findingsCount} Similar Findings`,
                similarFindingsToNotUpdate,
                scope,
                findingUpdateTaskType,
                updateType,
                tagCondition,
                sourceFinding: props.findingId,
                [props.type]: props[props.type],
            };

            if (showFindingsTable) {
                fields.push({
                    name: "findingsTable",
                    label: '',
                    render: () => <SimilarFindingsTable {...tableprops} />
                });
            }
        }

        return fields.filter(f => !_.isEmpty(f));
    }, [findingUpdateTaskType, findingsCount, props, rule, scope, showFindingsTable, tagCondition, updateType, scopeFields, featureFlags.VERSIONS]);

    const onOk = async (data) => {
        const _data = {
            applyOnFutureFindings: data.applyOnFutureFindings,
            applyToVersionFindings: data.applyToVersionFindings,
            scope,
            notes: data.note,
            updateType,
            contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
            equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
            [props.type === 'category' ?  'armorcodeCategory' : props.type]: props.type === 'severity' ? props.severity : props.category,
            findingId: props.findingId,
            ...(!_.isEmpty(similarFindingsToNotUpdate.current) ? { ignoreFindingIds: similarFindingsToNotUpdate.current } : {})
        };

        if(scope !== "FINDING") {
            await FindingAPI.similarFindings.update(_data, props.type).promise;
            message.success(<>Request accepted to change {_.capitalize(props.type)} for {findingsCount > 0 ? `${(findingsCount - (similarFindingsToNotUpdate.current?.length ?? 0 ))} findings` : props.findingId}. Please check after some time.</>);
        } else {
            await FindingAPI.updateFindingStatus[props.type](props.findingId, _data).promise;
            message.success(<>Successfully Changed {_.capitalize(props.type)} for {findingsCount > 0 ? `${findingsCount} findings` : props.findingId}. Please check after some time.</>);
        }

        thisRef.current.close();
    };

    const afterClose = () => {
        props.afterClose && props.afterClose();
    };

    const modalProps = {
        width: showFindingsTable ? MODAL_SIZE_ULTRA : MODAL_SIZE_SMALL,
        className: showFindingsTable ? "modal-full-height no-footer" : '',
        okButtonProps: {
            disabled: _.isUndefined(findingsCount)
        }
    }
    const isSeverityType = props.type === 'severity'
    return <BaseModalForm
        ref={thisRef}
        form={form}
        modalProps={modalProps}
        title={<strong>Change: {_.capitalize(props.type)} {isSeverityType ?  getVocabulary(props.oldValue) : props.oldValue} &#x2192;  {isSeverityType ? getVocabulary(props[props.type]) : props[props.type]}?</strong>}
        onOk={onOk}
        fields={fields}
        afterClose={afterClose}
        loading={loading}
        initialValues={{
            scope: "FINDING",
            applyOnFutureFindings: !isEmpty(rule),
            applyToVersionFindings: true
        }}
     />
});

export default SeverityCategoryChangeModal;